import { DealershipContactInfo, DisplayOperatingHour, OperatingHour } from "@/types/dealership/dealership.types";

const dayOrder = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const dayNameMapping = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

interface GroupedTime {
  days: string[];
  openTime?: string;
  closeTime?: string;
  closed: boolean;
}

export const groupOperatingHours = (
  data: OperatingHour[],
): DisplayOperatingHour[] => {
  if (!data) return [];
  const grouped = new Map<string, GroupedTime>();

  data.forEach((entry) => {
    const dayName = dayNameMapping.find((name) =>
      name.toLowerCase().startsWith(entry.day_of_the_week.toLowerCase()),
    );

    if (!dayName) return;

    if (entry.openTime === 'closed' || !entry.openTime) {
      const closedKey = 'closed';

      if (!grouped.has(closedKey)) {
        grouped.set(closedKey, { days: [], closed: true });
      }
      grouped.get(closedKey)!.days.push(dayName);
    } else {
      const key = `${entry.openTime}-${entry.closeTime}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          days: [] as string[],
          openTime: entry.openTime,
          closeTime: entry.closeTime || undefined,
          closed: false,
        });
      }
      grouped.get(key)!.days.push(dayName);
    }
  });

  const arrFromGrouped: GroupedTime[] = Array.from(grouped.values());

  arrFromGrouped.forEach((group) => {
    group.days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
  });

  const orderedGroupedHours = dayOrder.flatMap((day) =>
    arrFromGrouped.flatMap((group) =>
      group.days.includes(day) ? [{ ...group, day }] : [],
    ),
  );

  return orderedGroupedHours;
};

export const formatDealerPhoneNumberForTel = (
  phoneNumber?: string | null,
): string => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\s+/g, '').replace('(', '').replace(')', '');
};

export interface RawOperatingHourEntry {
  operating_hours_id: {
    id: number;
    day_of_the_week: string;
    opening_time: string | null;
    closing_time: string | null;
  };
}

export interface RawCityData {
  name: string;
  id: number;
  code: string;
  province: {
    id: number;
    name: string;
  };
  country?: {
    id: number;
    name: string;
  };
}

export interface RawDealerData {
  latitude: string;
  longitute: string;
  name: string;
  address_1: string | null;
  address_2: string | null;
  address_3: string | null;
  code: string | null;
  google_address: string | null;
  main_number: string | null;
  main_image?: string | null;
  id: string;
  cms_used_dealer_code?: string;
  cms_new_dealer_code?: string;

  operating_hours: RawOperatingHourEntry[];
  city: RawCityData | null;
  province?: {
    id: number;
    name: string;
    code?: string;
  } | null;
}

export interface ProcessedOperatingHour {
  day_of_the_week: string;
  openTime: string | null;
  closeTime: string | null;
}

export function getLatitude(dealerInfo: any): number {
  if (dealerInfo?.latitude) return Number(dealerInfo.latitude);
  if (dealerInfo?.lat) return Number(dealerInfo.lat);
  return 0;
}

export function getLongitude(dealerInfo: any): number {
  if (dealerInfo?.longitude) return Number(dealerInfo.longitude);
  if (dealerInfo?.longitute) return Number(dealerInfo.longitute);
  if (dealerInfo?.lng) return Number(dealerInfo.lng);
  return 0;
}

export function hasValidCoordinates(dealerInfo: any): boolean {
  return Boolean(
    (dealerInfo?.latitude || dealerInfo?.lat) &&
      (dealerInfo?.longitude || dealerInfo?.longitute || dealerInfo?.lng),
  );
}
export function mapRawDealerToContactInfo(
  rawData: RawDealerData,
): DealershipContactInfo {
  if (!rawData) {
    return {
      id: '',
      name: 'Data unavailable',
      operatingHours: [],
    } as DealershipContactInfo;
  }

  const processedOperatingHours: ProcessedOperatingHour[] = (
    rawData.operating_hours || []
  ).map((entry: RawOperatingHourEntry): ProcessedOperatingHour => {
    const details = entry.operating_hours_id;

    return {
      day_of_the_week: details.day_of_the_week,
      openTime: details.opening_time,
      closeTime: details.closing_time,
    };
  });

  let combinedSecondaryAddress = rawData.address_2;

  if (rawData.address_3) {
    combinedSecondaryAddress = combinedSecondaryAddress
      ? `${combinedSecondaryAddress}, ${rawData.address_3}`
      : rawData.address_3;
  }

  return {
    id: rawData.id,
    name: rawData.name,
    latitude: rawData.latitude,
    longitude: rawData.longitute,
    mainAddress: rawData.address_1,
    secondaryAddress: combinedSecondaryAddress,
    city: rawData.city
      ? {
          name: rawData.city.name,
          province: rawData.city.province
            ? {
                name: rawData.city.province.name,
              }
            : null,
        }
      : null,
    code: rawData.code,
    google_address: rawData.google_address,
    phoneNumber: rawData.main_number,
    operatingHours: processedOperatingHours as unknown as OperatingHour[],
  };
}
